import React, { useEffect, useRef, useState } from 'react';
import { Button, SuccessTick, Pill } from './ui';
import type { ParsedData } from '../types';
import { extractPortfolioSummary } from '../utils/extractSummary';

/**
 * Success summary screen shown after a successful import. Builds trust by
 * showing *what* we imported (total value, folios, holdings) before the
 * modal closes. Auto-closes after a configurable delay unless a CTA is
 * provided.
 */

interface SuccessSummaryProps {
  data: ParsedData;
  autoCloseMs?: number;
  cta?: { label: string; onClick: () => void };
  onClose: () => void;
}

const DEFAULT_AUTO_CLOSE = 10000;

export const SuccessSummary: React.FC<SuccessSummaryProps> = ({
  data,
  autoCloseMs = DEFAULT_AUTO_CLOSE,
  cta,
  onClose,
}) => {
  const summary = extractPortfolioSummary(data);

  // Dev-mode hint: if nothing extractable is found, log the top-level
  // keys so embedders can see what the SDK is looking at.
  useEffect(() => {
    if (
      typeof process !== 'undefined' &&
      process.env?.NODE_ENV !== 'production' &&
      summary.totalValue === null &&
      summary.folios === null &&
      summary.holdings === null
    ) {
      // eslint-disable-next-line no-console
      console.debug(
        '[SuccessSummary] Could not extract totals from response.',
        'Top-level keys:',
        Object.keys((data as any) || {}),
      );
    }
  }, [data, summary.totalValue, summary.folios, summary.holdings]);
  const [remaining, setRemaining] = useState(Math.ceil(autoCloseMs / 1000));

  // Store onClose in a ref so the timer always invokes the latest callback.
  // Avoids the classic stale-closure foot-gun (CLAUDE.md: "Always use useRef
  // for values in setInterval/setTimeout").
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (autoCloseMs <= 0) return;
    const tick = setInterval(() => {
      setRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    const close = setTimeout(() => onCloseRef.current(), autoCloseMs);
    return () => {
      clearInterval(tick);
      clearTimeout(close);
    };
  }, [autoCloseMs]);

  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <SuccessTick size={56} />
      <h3
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: 'var(--pc-color-text)',
          margin: '14px 0 6px',
        }}
      >
        {summary.totalValue !== null
          ? 'Portfolio imported'
          : summary.folios || summary.holdings
          ? 'Statement imported'
          : 'All set'}
      </h3>
      {summary.asOn && (
        <p
          style={{
            fontSize: 12,
            color: 'var(--pc-color-text-muted)',
            margin: '0 0 18px',
          }}
        >
          As on {formatDate(summary.asOn)}
        </p>
      )}

      {summary.totalValue !== null && (
        <div
          style={{
            fontSize: 32,
            fontWeight: 700,
            color: 'var(--pc-color-text)',
            letterSpacing: '-0.02em',
            marginBottom: 12,
          }}
        >
          {formatCurrency(summary.totalValue)}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 8,
          marginBottom: 22,
          flexWrap: 'wrap',
        }}
      >
        {summary.casType && <Pill tone="primary">{summary.casType}</Pill>}
        {/* Only render counts > 0. An MF-only import has 0 demat
            holdings and a demat-only import has 0 folios — surfacing
            the zero pill is noise. */}
        {summary.folios !== null && summary.folios > 0 && (
          <Pill tone="neutral">
            {summary.folios} folio{summary.folios === 1 ? '' : 's'}
          </Pill>
        )}
        {summary.holdings !== null && summary.holdings > 0 && (
          <Pill tone="neutral">
            {summary.holdings} holding{summary.holdings === 1 ? '' : 's'}
          </Pill>
        )}
      </div>

      {summary.investorName && (
        <p
          style={{
            fontSize: 13,
            color: 'var(--pc-color-text-muted)',
            margin: '0 0 20px',
          }}
        >
          {summary.investorName}
        </p>
      )}

      {cta ? (
        <Button
          variant="primary"
          fullWidth
          onClick={() => {
            cta.onClick();
            onClose();
          }}
        >
          {cta.label}
        </Button>
      ) : (
        <Button variant="primary" fullWidth onClick={onClose}>
          Done
        </Button>
      )}

      {autoCloseMs > 0 && remaining > 0 && (
        <p
          style={{
            fontSize: 11,
            color: 'var(--pc-color-text-faded)',
            margin: '12px 0 0',
          }}
          aria-live="polite"
        >
          Auto-closing in {remaining}s
        </p>
      )}
    </div>
  );
};

// ---------- Display helpers ----------

function formatCurrency(value: number): string {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `₹ ${Math.round(value).toLocaleString('en-IN')}`;
  }
}

function formatDate(input: string): string {
  try {
    const d = new Date(input);
    if (Number.isNaN(d.getTime())) return input;
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return input;
  }
}
